import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { QrCode } from "lucide-react";
import { api, errMsg, formatIDR, formatDuration } from "@/lib/api";
import { Countdown, StatusBadge } from "@/components/shared";

function QrPlaceholder() {
  const cells = Array.from({ length: 169 }, (_, i) => ((i * 7919) % 11) < 5);
  return (
    <div className="w-52 h-52 p-3 bg-white border border-border rounded-xl grid grid-cols-13 gap-[2px]" style={{ gridTemplateColumns: "repeat(13, 1fr)" }} data-testid="qris-placeholder">
      {cells.map((on, i) => <span key={i} className={`rounded-[2px] ${on ? "bg-slate-800" : "bg-transparent"}`} />)}
    </div>
  );
}

export default function Pay() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => api.get(`/orders/${code}`).then((r) => setOrder(r.data)).catch((e) => toast.error(errMsg(e))), [code]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (order && order.status !== "awaiting_payment") navigate(`/order/${code}`, { replace: true }); }, [order, code, navigate]);

  const simulate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/payments/${code}/simulate`);
      toast.success(data.message);
      navigate(`/order/${code}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Batalkan order ini? Slot talent akan dilepas.")) return;
    try { await api.post(`/orders/${code}/cancel`); toast.success("Order dibatalkan"); navigate(`/order/${code}`); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!order) return <div className="py-24 text-center text-slate-500">Memuat order...</div>;

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <p className="eyebrow mb-3">Pembayaran</p>
      <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-slate-800">Scan QRIS untuk bayar</h1>
      <div className="mt-8 card-soft p-6 sm:p-8">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div><p className="text-sm text-slate-500">Kode order</p><p className="font-heading text-2xl font-semibold text-slate-800 tracking-wide" data-testid="pay-order-code">{order.code}</p></div>
          <StatusBadge status={order.status} />
        </div>
        <div className="mt-6 flex flex-col items-center">
          <QrPlaceholder />
          <p className="mt-4 text-sm text-slate-500">Total</p>
          <p className="font-heading text-3xl font-semibold text-slate-800" data-testid="pay-amount">{formatIDR(order.price)}</p>
          <p className="text-sm text-slate-500 mt-1">{order.service_name} · {formatDuration(order.duration_minutes)} · {order.talent_name}</p>
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">Selesaikan dalam</p>
            <Countdown target={order.expires_at} className="text-2xl text-slate-800" onDone={load} />
          </div>
        </div>
        <div className="mt-8 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900" data-testid="pay-sandbox-note">
          Mode sandbox: payment gateway QRIS asli belum disambungkan. Tombol di bawah menirukan callback webhook pembayaran berhasil.
        </div>
        <button onClick={simulate} disabled={loading} className="btn-primary w-full mt-4" data-testid="pay-simulate-button">
          <QrCode size={18} strokeWidth={1.5} />{loading ? "Memproses..." : "Simulasi Bayar (Sandbox)"}
        </button>
        <button onClick={cancel} className="btn-ghost w-full mt-2 text-sm text-slate-500" data-testid="pay-cancel-button">Batalkan order</button>
        <p className="mt-4 text-xs text-slate-500 text-center">Simpan kode order kamu. <Link to={`/order/${order.code}`} className="underline" data-testid="pay-status-link">Lihat status order</Link></p>
      </div>
    </div>
  );
}
